import { useState } from "react";
import { CoreModal, CoreModalDescription } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { isInstituteProfile } from "@verity/proctoring-option";
import SaveProfileModal, { SaveProfileModalProps } from "./SaveProfileModal";
import i18nPackage from "../../i18n/i18n";
import { getProfileName } from "../profile";

type SaveProfileWrapperProps = {
	activeProfile: ProctoringOptionProfileV2 | undefined;
	onClose: () => void;
	hasInstituteProfilePermissions: boolean;
	canPublishToCourse: boolean;
	isThirdPartyEnabled: boolean;
	onSaveNewProfile: SaveProfileModalProps["onSave"];
	onUpdateProfile: () => void;
};

const SaveProfileWrapper = ({
	activeProfile,
	onClose,
	onSaveNewProfile,
	onUpdateProfile,
	hasInstituteProfilePermissions,
	canPublishToCourse,
	isThirdPartyEnabled,
}: SaveProfileWrapperProps) => {
	const [createProfileModalShowing, setCreateProfileModalShowing] = useState(false);
	const isMobileLayout = useMobileLayout();

	const shouldShowCreateProfile = () => {
		// if institute profile but not admin, only allow save new profile (no edit)
		if (activeProfile && isInstituteProfile(activeProfile) && !hasInstituteProfilePermissions) {
			return true;
		}
		return !activeProfile || createProfileModalShowing;
	};

	return (
		<>
			{shouldShowCreateProfile() ? (
				<SaveProfileModal
					onSave={onSaveNewProfile}
					isThirdPartyEnabled={isThirdPartyEnabled}
					onClose={onClose}
					canPublishToCourse={canPublishToCourse}
					hasInstituteProfilePermissions={hasInstituteProfilePermissions}
				/>
			) : (
				<CoreModal
					title={
						isMobileLayout
							? i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.titleShort")
							: i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.title")
					}
					open={true}
					onClose={onClose}
					minWidth={isMobileLayout ? "100%" : "min(60%, 600px)"}
					widthFixed
					buttons={[
						{
							text: i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.no"),
							color: "secondary",
							onClick: onClose,
							hide: isMobileLayout,
						},
						{
							text: i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.createNewProfile"),
							color: "primary",
							onClick: () => {
								setCreateProfileModalShowing(true);
							},
						},
						{
							text: i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.updateProfile"),
							color: "primary",
							onClick: onUpdateProfile,
							hide: shouldShowCreateProfile(),
						},
					]}
				>
					<CoreModalDescription>
						{i18nPackage.t("editQuizPage.saveProfileModalTSX.updateModal.description", {
							profileName: getProfileName(activeProfile),
						})}
					</CoreModalDescription>
				</CoreModal>
			)}
		</>
	);
};

export default SaveProfileWrapper;
